import jsPDF from "jspdf";
import { resolveE2EUserDisplayNameWithOrg } from "./e2eUserProfiles";
import { getReviewingDepartments } from "./e2eReviewingDepartment";

/**
 * Adds wrapped text to PDF and returns the new Y position
 * Handles page breaks for long text blocks
 */
const addWrappedText = (doc, text, x, y, maxWidth, lineHeight = 6) => {
  const pageHeight = doc.internal.pageSize.height;
  const pageBreakThreshold = pageHeight - 15; // Leave 15mm margin at bottom
  
  const lines = doc.splitTextToSize(text || "", maxWidth);
  lines.forEach((line) => {
    // Check if we need a page break before writing this line
    if (y > pageBreakThreshold) {
      doc.addPage();
      y = 15; // Start near top of new page
    }
    doc.text(line, x, y);
    y += lineHeight;
  });
  return y;
};

/**
 * Adds a question block to the PDF
 */
const addQuestionBlock = (doc, question, index, x, y, contentWidth) => {
  const pageHeight = doc.internal.pageSize.height;
  const pageBreakThreshold = pageHeight - 30; // Reserve 30mm at bottom
  
  const answerText = question.answerText || "";
  const selectedOption = question.selectedOption;
  const selectedOptions = question.selectedOptions || [];

  // Check if we need a page break before starting this question
  if (y > pageBreakThreshold) {
    doc.addPage();
    y = 15;
  }

  if (question.questionDescription) {
    doc.setFont("helvetica", "normal");
    y = addWrappedText(doc, question.questionDescription, x, y, contentWidth);
    y += 1;
  }

  doc.setFont("helvetica", "bold");
  y = addWrappedText(doc, `${index + 1}. ${question.questionText}`, x, y, contentWidth);

  doc.setFont("helvetica", "normal");
  if (answerText) {
    y = addWrappedText(doc, `Svar: ${answerText}`, x + 2, y, contentWidth - 2);
  }

  if (selectedOption) {
    y = addWrappedText(doc, `Svar: ${selectedOption.optionText}`, x + 2, y, contentWidth - 2);
    if (selectedOption.followUpQuestion) {
      y = addWrappedText(
        doc,
        `Foljdfraga: ${selectedOption.followUpQuestion.questionText}`,
        x + 6,
        y,
        contentWidth - 6
      );
      y = addWrappedText(
        doc,
        `Svar: ${selectedOption.followUpQuestion.answerText || "-"}`,
        x + 8,
        y,
        contentWidth - 8
      );
    }
  }

  if (selectedOptions.length > 0) {
    y = addWrappedText(doc, "Svar:", x + 2, y, contentWidth - 2);
    selectedOptions.forEach((option) => {
      y = addWrappedText(doc, `- ${option.optionText}`, x + 6, y, contentWidth - 6);
      if (option.followUpQuestion) {
        y = addWrappedText(
          doc,
          `Foljdfraga: ${option.followUpQuestion.questionText}`,
          x + 10,
          y,
          contentWidth - 10
        );
        y = addWrappedText(
          doc,
          `Svar: ${option.followUpQuestion.answerText || "-"}`,
          x + 12,
          y,
          contentWidth - 12
        );
      }
    });
  }

  return y + 2;
};

/**
 * Adds a section and its questions to the PDF
 * Returns the new Y position
 */
const addSectionToPdf = (doc, section, level, y) => {
  const pageHeight = doc.internal.pageSize.height;
  const pageBreakThreshold = pageHeight - 25; // Reserve 25mm at bottom
  const marginX = 14;
  const contentWidth = 182;
  const sectionX = marginX + level * 6;
  const sectionHeader = `${section.sectionCode || ""} ${section.sectionTitle || ""}`.trim();

  // Check if section header won't fit on current page
  if (y > pageBreakThreshold) {
    doc.addPage();
    y = 15;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  y = addWrappedText(doc, sectionHeader, sectionX, y, contentWidth - level * 6);

  if (section.sectionDescription) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    y = addWrappedText(doc, section.sectionDescription, sectionX, y, contentWidth - level * 6);
  }

  y += 2;
  doc.setFontSize(11);

  (section.questions || []).forEach((question, index) => {
    y = addQuestionBlock(
      doc,
      question,
      index,
      sectionX + 4,
      y,
      contentWidth - level * 6 - 4
    );
  });

  (section.children || []).forEach((childSection) => {
    y = addSectionToPdf(doc, childSection, level + 1, y + 1);
  });

  return y + 2;
};

/**
 * Exports a submission to PDF format
 * @param {Object} submission - The submission object with form and answers data
 * @param {string} submissionId - The submission ID for the filename
 * @param {Function} formatDateTimeNoSeconds - Function to format dates
 * @param {Function} getSubmissionStatusLabel - Function to get status labels
 * @param {Object} userProfilesById - User profile data keyed by user ID
 */
export const exportSubmissionToPdf = (
  submission,
  submissionId,
  formatDateTimeNoSeconds,
  getSubmissionStatusLabel,
  userProfilesById = {}
) => {
  if (!submission || submission.status !== "APPROVED") {
    return;
  }

  const doc = new jsPDF();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("E2E Inlamning", 14, 10);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);

  let y = 20;
  y = addWrappedText(doc, `Formular: ${submission.form?.formTitle || "-"}`, 14, y, 182);
  y = addWrappedText(doc, `Status: ${getSubmissionStatusLabel(submission.status)}`, 14, y, 182);
  y = addWrappedText(
    doc,
    `Inskickad: ${submission.submittedAt ? formatDateTimeNoSeconds(submission.submittedAt) : "-"}`,
    14,
    y,
    182
  );

  // Add user name if available
  if (submission.userId) {
    const userName = resolveE2EUserDisplayNameWithOrg(submission.userId, userProfilesById);
    y = addWrappedText(doc, `Inlamnad av: ${userName}`, 14, y, 182);
  }

  // The tester export names the handling department, never the individual reviewer
  const reviewingDepartments = getReviewingDepartments(submission);
  if (reviewingDepartments.length > 0) {
    y = addWrappedText(doc, `Handlaggs av: ${reviewingDepartments.join(", ")}`, 14, y, 182);
  }

  if (submission.form?.description) {
    y += 1;
    y = addWrappedText(doc, submission.form.description, 14, y, 182);
  }

  y += 4;

  // Add sections, each on its own page
  (submission.form?.sections || []).forEach((section) => {
    y = addSectionToPdf(doc, section, 0, y);
  });

  const safeFormTitle = (submission.form?.formTitle || "inlamning")
    .replace(/[^a-zA-Z0-9-_]/g, "_")
    .slice(0, 40);
  doc.save(`${safeFormTitle}_${submission.submissionId || submissionId}.pdf`);
};
